import React, { useEffect } from "react"
import PropTypes from "prop-types"
import { defineMessages } from "react-intl"
import Box from "@material-ui/core/Box"
import Button from "@material-ui/core/Button"
import { Popover } from "@material-ui/core"
import SearchIcon from "@material-ui/icons/Search"
import PopupState, { bindTrigger, bindPopover } from "material-ui-popup-state"

import { useDispatch, useSelector } from "react-redux"
import ArrowBackIcon from "@material-ui/icons/ArrowBack"
import { reset } from "redux-form"
import PageSubTitle from "../../component/header/PageSubTitle"
import withTabMenu, { findByLocation } from "../admin/withTabMenu"
import DocumentFormsSearchForm from "./DocumentFormsSearchForm"
import DocumentList from "./DocumentList"
import documentSlice from "./documentSlice"
import useSmartFormat from "../../i18n/useSmartFormat"
import CircularProgressBox from "../../component/CircularProgressBox"
import { words } from "../../i18n/lstrings"
import { CancelToken } from "../../api/httpClient"
import documentsSubMenu from "./documentsSubMenu"

const { fetchDocumentsView, search, goToDocumentsSummary, clearDocuments } =
  documentSlice.actions

const messages = defineMessages({
  search: {
    id: "word.search",
    defaultMessage: "Search"
  },
  searchTitle: {
    id: "form.page.title",
    defaultMessage: "Your forms"
  },
  withYourAttentionTitle: {
    id: "form.your.attention.title",
    defaultMessage: "Forms that need your attention"
  },
  withYourAttentionDescription: {
    id: "form.your.attention.description",
    defaultMessage: "These forms require your action."
  },
  recentDocumentTitle: {
    id: "form.recent.title",
    defaultMessage: "Forms added recently"
  },
  recentDocumentDescription: {
    id: "form.recent.description",
    defaultMessage: "These forms are always available and/or downloadable."
  }
})

/**
 * This component is used to render the received Forms page for a connected user on the Documents page
 * */
function DocumentsFormsPage(props) {
  const { children } = props
  const format = useSmartFormat()
  const dispatch = useDispatch()
  const loading = useSelector((state) => state.documents.loading)
  const searchDocuments = useSelector((state) => state.documents.searchDoc)
  const needYourAttentionDoc = useSelector(
    (state) => state.documents.needYourAttentionDoc
  )
  const recentlyAddedDoc = useSelector(
    (state) => state.documents.recentlyAddedDoc
  )
  const isSearchView = useSelector((state) => state.documents.view)

  /**
   * Run effects only when first mounting
   */
  useEffect(() => {
    const { token, cancel } = CancelToken.source()
    dispatch(
      fetchDocumentsView(token, {
        category: "forms"
      })
    )
    return () => {
      cancel()
      dispatch(clearDocuments())
    }
  }, [dispatch])

  function onReturnClick() {
    dispatch(goToDocumentsSummary())
    dispatch(reset("DocumentFormsSearchForm"))
  }

  return (
    <Box>
      {children}
      <Box height={45} display="flex" justifyContent="space-between" mt={2}>
        <Box>
          {isSearchView === 1 && (
            <Button
              variant="outlined"
              onClick={onReturnClick}
              startIcon={<ArrowBackIcon />}
            >
              {format(words.return)}
            </Button>
          )}
        </Box>
        <PopupState variant="popover" popupId="document-search-form">
          {(popupState) => {
            const submit = (forms) => {
              popupState.setOpen(false)

              // build query args from Redux Form obj
              dispatch(search({ ...forms, category: "forms" }))
            }

            return (
              <Box>
                <Button
                  variant="outlined"
                  startIcon={<SearchIcon />}
                  {...bindTrigger(popupState)}
                >
                  {format(messages.search)}
                </Button>
                <Popover
                  {...bindPopover(popupState)}
                  anchorOrigin={{
                    vertical: "bottom",
                    horizontal: "center"
                  }}
                  transformOrigin={{
                    vertical: "top",
                    horizontal: "center"
                  }}
                >
                  <DocumentFormsSearchForm onSubmit={submit} />
                </Popover>
              </Box>
            )
          }}
        </PopupState>
      </Box>

      {loading && <CircularProgressBox height="120px" />}
      {!loading && isSearchView === 0 && (
        <>
          <Box mb={5}>
            <PageSubTitle
              subtitle={format(messages.withYourAttentionTitle)}
              description={format(messages.withYourAttentionDescription)}
            />
            <DocumentList model={needYourAttentionDoc} />
          </Box>

          <Box>
            <PageSubTitle
              subtitle={format(messages.recentDocumentTitle)}
              description={format(messages.recentDocumentDescription)}
            />
            <DocumentList model={recentlyAddedDoc} />
          </Box>
        </>
      )}
      {!loading && isSearchView === 1 && (
        <Box>
          <PageSubTitle subtitle={format(messages.searchTitle)} />
          <DocumentList model={searchDocuments} />
        </Box>
      )}
    </Box>
  )
}

DocumentsFormsPage.propTypes = {
  children: PropTypes.any.isRequired
}

export default (props) => {
  const formsNb = useSelector((state) => state.documents.formsNb)
  function getPlaceholders() {
    return { formsNb }
  }
  return withTabMenu(
    DocumentsFormsPage,
    documentsSubMenu,
    findByLocation,
    getPlaceholders
  )(props)
}
